<template lang="pug">
  form.prose.dark_prose-invert.p-8.bg-gray-100.dark_bg-gray-800.mb-10.relative
    button.btn.btn-icon.opacity-50.aspect-square.flex.items-center.justify-center.font-bold.text-xl.w-8.h-8.absolute.top-4.right-4(
      @click="emit('close')"
      ref="formRef"
      type="button"
    ) &times;
    h2.mt-2.mb-10 Course tests

    hr.my-6

    h3 Check style guide
    p.opacity-80 Select up to {{ maxCourseIds }} courses from those shown below to check against #[a(href="https://docs.google.com/document/d/1gDzgQEt_n4xdm-p-8Ydb5y5nx1nHD1KF/" target="_blank") the VinciWorks course style guide]. An excel file will be downloaded containing the results once the comparison is complete. Tests are not currently available for Conversational Learning courses.
    
    .flex.items-center.justify-between.mb-4
      button.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
        type="button"
        @click="proofCourse"
        :disabled="selectedCourseIds.length === 0 || selectedCourseIds.length > maxSelected || isComparing"
      )
        span.group-disabled_opacity-50 {{ isComparing ? 'Processing...' : 'Download report' }}

      button.btn-hover_cursor-pointer.bg-red-600.btn-hover_bg-red-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
        @click="clearSelectedCourses"
        :disabled="selectedCourseIds.length === 0 || selectedCourseIds.length > maxSelected || isComparing"
      )
        span.group-disabled_opacity-50 Clear Selection

    .text-sm.text-gray-600.dark_text-gray-400.mb-4(v-if="selectedCourseIds.length > 0")
      strong Selected courses ({{ selectedCourseIds.length }}/{{ maxCourseIds }}):
      span.ml-2 {{ selectedCourseNames.join(', ') }}

    // Progress indicator
    .mt-4(v-if="isComparing")
      .flex.items-center.gap-3
        .animate-spin.w-5.h-5.border-2.border-primary-600.border-t-transparent.rounded-full
        span.text-current {{ progressMessage }}
    
    // Success message with details
    .mt-4.p-4.bg-green-50.dark_bg-green-900.border.border-green-200.dark_border-green-700.rounded-lg(v-if="successMessage")
      .flex.items-start.gap-2
        .text-green-600.dark_text-green-400.mt-1
          svg.w-5.h-5(fill="currentColor" viewBox="0 0 20 20")
            path(fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd")
        .flex-1
          p.text-green-800.dark_text-green-200.font-medium {{ successMessage }}
          p.text-green-600.dark_text-green-400.text-sm.mt-1(v-if="lastDownloadedCourses.length > 0")
            | Courses processed: {{ lastDownloadedCourses.join(', ') }}

    // Error message with details  
    .mt-4.p-4.bg-red-50.dark_bg-red-900.border.border-red-200.dark_border-red-700.rounded-lg(v-if="errorMessage")
      .flex.items-start.gap-2
        .text-red-600.dark_text-red-400.mt-1
          svg.w-5.h-5(fill="currentColor" viewBox="0 0 20 20")
            path(fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd")
        .flex-1
          p.text-red-800.dark_text-red-200.font-medium Error occurred
          p.text-red-600.dark_text-red-400.text-sm {{ errorMessage }}
          button.mt-2.text-sm.text-red-600.dark_text-red-400.underline.hover_no-underline(@click="clearErrors") Dismiss
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { checkStyleGuide } from '../lib/callModContentAuthors';
import createResportCSV, { downloadCSV } from '../lib/createResportCSV';

const emit = defineEmits(['close', 'clearSelectedCourses']);

const props = defineProps({
  selectedCourseIds: {
    type: Array as () => string[], // These are course names like "FIRM_123" or "123"
    default: () => []
  },
  maxSelected: Number
});

const maxCourseIds = 12; // max number of courses to compare
const isComparing = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const progressMessage = ref('');
const lastDownloadedCourses = ref<string[]>([]);

// Computed property to show selected course identifiers
const selectedCourseNames = computed(() => {
  return props.selectedCourseIds;
});

// Helper function to parse course name and get course info
const parseCourseIdentifier = (courseName: string) => {
  const parts = courseName.split('_');
  let courseId: number;
  let firmCode: string | undefined;

  if (parts.length === 2) {
    // Format: "FIRM_123"
    firmCode = parts[0];
    courseId = parseInt(parts[1]);
  } else {
    // Format: "123"
    courseId = parseInt(courseName);
    firmCode = undefined;
  }

  return {
    courseId,
    firmCode,
    identifier: courseName
  };
};

// Clear error and success messages
const clearErrors = () => {
  errorMessage.value = '';
};

const clearMessages = () => {
  errorMessage.value = '';
  successMessage.value = '';
  lastDownloadedCourses.value = [];
};

// Auto-clear success message after delay
const showSuccessMessage = (message: string, courses: string[]) => {
  successMessage.value = message;
  lastDownloadedCourses.value = courses;

  // Auto-clear after 10 seconds
  setTimeout(() => {
    if (successMessage.value === message) {
      successMessage.value = '';
      lastDownloadedCourses.value = [];
    }
  }, 10000);
};

const proofCourse = async () => {
  if (props.selectedCourseIds.length === 0) return;
  if (props.selectedCourseIds.length > maxCourseIds) {
    errorMessage.value = `You can select a maximum of ${maxCourseIds} courses.`;
    return;
  }

  isComparing.value = true;
  clearMessages();
  progressMessage.value = 'Analyzing courses against style guide...';

  try {
    const courseIds = props.selectedCourseIds;
    console.log('Checking style guide for courses:', courseIds);

    // Call the style guide API
    progressMessage.value = 'Calling style guide analysis API...';
    const response = await checkStyleGuide(courseIds);

    console.log('Style guide response:', response);

    // Handle the response - it might be a single report or multiple reports
    progressMessage.value = 'Processing analysis results...';
    let csvContent = '';

    if (Array.isArray(response.data)) {
      // Multiple courses - concatenate all reports
      for (let i = 0; i < response.data.length; i++) {
        progressMessage.value = `Processing course ${i + 1} of ${response.data.length}...`;

        const report = response.data[i];
        const courseName = props.selectedCourseIds[i];
        const { courseId, firmCode, identifier } =
          parseCourseIdentifier(courseName);

        const courseCsv = await createResportCSV(
          courseId,
          firmCode || '',
          identifier, // Use the identifier as the course name
          'styleGuide',
          report
        );

        if (i > 0) {
          csvContent += '\n\n'; // Separator between courses
        }
        csvContent += courseCsv;
      }
    } else {
      // Single course or combined report
      const report = response.data;

      // If multiple courses were selected, create a combined report
      if (props.selectedCourseIds.length > 1) {
        const courseIdsString = props.selectedCourseIds.join(' | ');

        csvContent = await createResportCSV(
          0, // Use 0 for combined reports
          '',
          `Combined Report (Course IDs: ${courseIdsString})`,
          'styleGuide',
          report
        );
      } else {
        // Single course
        const courseName = props.selectedCourseIds[0];
        const { courseId, firmCode, identifier } =
          parseCourseIdentifier(courseName);

        csvContent = await createResportCSV(
          courseId,
          firmCode || '',
          identifier, // Use the identifier as the course name
          'styleGuide',
          report
        );
      }
    }

    // Generate filename
    progressMessage.value = 'Preparing download...';
    const timestamp = new Date().toISOString().split('T')[0];
    const filename =
      props.selectedCourseIds.length > 1
        ? `style-guide-report-combined-${timestamp}.csv`
        : `style-guide-report-${props.selectedCourseIds[0]}-${timestamp}.csv`;

    // Download the CSV
    downloadCSV(csvContent, filename);

    // Show enhanced success message
    const courseDisplayNames = selectedCourseNames.value;
    showSuccessMessage(
      `Style guide report downloaded successfully! (${filename})`,
      courseDisplayNames
    );
  } catch (error) {
    console.error('Error checking style guide:', error);

    // Provide more specific error messages
    let userFriendlyError =
      'An error occurred while generating the style guide report.';

    if (
      error.message?.includes('Network Error') ||
      error.message?.includes('fetch')
    ) {
      userFriendlyError =
        'Network error: Please check your connection and try again.';
    } else if (error.message?.includes('timeout')) {
      userFriendlyError =
        'Request timed out: The analysis is taking longer than expected. Please try with fewer courses.';
    } else if (error.message?.includes('Course information not found')) {
      userFriendlyError =
        'Course information not found. Please refresh the page and try again.';
    } else if (error.message?.includes('No CSV results found')) {
      userFriendlyError =
        'No style guide findings were returned. The courses may already be perfectly compliant!';
    } else if (error.message) {
      userFriendlyError = error.message;
    }

    errorMessage.value = userFriendlyError;
  } finally {
    isComparing.value = false;
    progressMessage.value = '';
  }
};

const clearSelectedCourses = () => {
  emit('clearSelectedCourses');
  clearMessages(); // Clear any existing messages when clearing selection
};
</script>
